import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { isSlaStage, SlaStage } from "../packages";
import { MomentType } from "./types";
import { TEXT_ERROR } from "./validation";

const dataOf = (e: unknown): Record<string, unknown> =>
  ((e as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {}) as Record<string, unknown>;
const codeOf = (e: unknown): string => getApiErrorCode(e) ?? "";

export type SlaRequirement = { stage: SlaStage; overMinutes: number };

/** 422 `SLA_JUSTIFICATION_REQUIRED` com `extra: {stage, overMinutes}` → requisito da janela (c). */
export function slaRequirementOf(error: unknown): SlaRequirement | null {
  if (codeOf(error) !== "SLA_JUSTIFICATION_REQUIRED") return null;
  const { stage, overMinutes } = dataOf(error) as { stage?: unknown; overMinutes?: unknown };
  if (!isSlaStage(stage)) return null;
  return { stage, overMinutes: Math.max(0, Number(overMinutes) || 0) };
}

/** O BE pede uma janela que não veio no corpo (motivo ausente). */
export const isReasonRequired = (error: unknown) => ["REASON_REQUIRED", "FRICTION_REQUIRED"].includes(codeOf(error));

const MOMENTS: MomentType[] = ["cancellation", "refusal", "sla_breach", "friction"];
/** Erros de motivo que o usuário corrige na própria janela (texto curto, motivo desativado). */
const FIXABLE = ["REASON_TEXT_REQUIRED", "REASON_INVALID", "REASON_TEXT_TOO_LONG"];

/**
 * Qual janela o erro aponta: `extra.moment` do BE (`friction` | `sla_breach` | `refusal` | `cancellation`),
 * `FRICTION_REQUIRED` → atrito, `SLA_JUSTIFICATION_REQUIRED` → SLA. Sem pista → `null` (o chamador decide).
 */
export function captureMomentOf(error: unknown): MomentType | null {
  const code = codeOf(error);
  if (code === "SLA_JUSTIFICATION_REQUIRED") return "sla_breach";
  if (code === "FRICTION_REQUIRED") return "friction";
  if (code !== "REASON_REQUIRED" && !FIXABLE.includes(code)) return null;
  const moment = dataOf(error).moment;
  return MOMENTS.includes(moment as MomentType) ? (moment as MomentType) : null;
}

/** Erro que se resolve dentro de uma janela já preenchida (reabre a janela com a mensagem). */
export const isFixableCaptureError = (error: unknown) => FIXABLE.includes(codeOf(error));

const MESSAGES: Record<string, string> = {
  REASON_TEXT_REQUIRED: TEXT_ERROR,
  REASON_TEXT_TOO_LONG: "O detalhe pode ter no máximo 2000 caracteres.",
  REASON_INVALID: "Esse motivo não está mais disponível. Reabra a janela e escolha outro.",
  VACANCY_INVALID: "Algum plantão não pertence mais a este pacote. Atualize a lista.",
  PACKAGE_NOT_EDITABLE: "Este pacote mudou de situação. Atualize a lista.",
};

export const captureErrorMessage = (error: unknown, fallback: string) => MESSAGES[codeOf(error)] ?? getApiErrorMessage(error, fallback);
